// @ts-check
"use strict";

import * as Sentry from "@sentry/react";
import { useQuery } from '@tanstack/react-query';

/**
 * @typedef {Object} LinkPreviewData
 * @property {string} originalUrl - The original URL that was requested
 * @property {string} url - The resolved base URL
 * @property {string} domain - The domain extracted from the URL
 * @property {string|null} title - The page title
 * @property {string|null} description - The page description
 * @property {string|null} image - The preview image URL
 * @property {string|null} favicon - The favicon URL
 * @property {string|null} siteName - The site name
 */

/**
 * @typedef {Object} LinkPreviewResponse
 * @property {string} message - Response message
 * @property {Object} data - Response data
 * @property {LinkPreviewData} data.preview - The preview data
 */

/**
 * @typedef {Object} LinkPreviewError
 * @property {string} message - Error message
 */

/**
 * Fetch link preview data from the API
 * @param {string} url - The URL to get preview data for
 * @returns {Promise<LinkPreviewData>}
 */
async function fetchLinkPreview(url) {
  // @ts-ignore
  const baseURL = import.meta.env.VITE_SOCKET_URL;
  
  const response = await fetch(new URL(`link-preview?url=${encodeURIComponent(url)}`, baseURL), {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ message: 'Failed to fetch link preview' }));
    const error = new Error(errorData.message || `HTTP error! status: ${response.status}`, {
      cause: {
        status: response.status
      }
    });
    throw error
  }

  /** @type {LinkPreviewResponse} */
  const data = await response.json();
  return data.data.preview;
}

/** @param {string} url */
export function getFallbackData(url) {
  return /** @type {LinkPreviewData} */ ({
    originalUrl: url,
    url,
    domain: getDomainFallback(url),
    title: null,
    description: null,
    image: null,
    favicon: getFaviconFallback(url),
    siteName: null,
  })
}

/**
 * React Query hook for fetching link preview data
 * @param {string} url - The URL to get preview data for
 * @param {Object} [options] - Additional options
 * @param {boolean} [options.enabled=true] - Whether the query should be enabled
 * @returns {import('@tanstack/react-query').UseQueryResult<LinkPreviewData, Error>}
 */
export function useLinkPreview(url, options = {}) {
  const { enabled = true } = options;

  return useQuery({
    queryKey: ['linkPreview', url],
    queryFn: () => fetchLinkPreview(url),
    enabled: enabled && !!url,
    staleTime: Infinity,
    retry: (failureCount, error) => {
      // Don't retry on 400 errors (bad request)
      if (error.cause?.status === 400) {
        return false;
      }
      // Retry up to 2 times for other errors
      return failureCount < 2;
    },
  });
}

/**
 * Fallback function to extract domain from URL (used when API fails)
 * @param {string} url 
 * @returns {string}
 */
export function getDomainFallback(url) {
  try {
    return new URL(url).hostname.replace('www.', '');
  } catch (error) {
    Sentry.captureException(error, {
      extra: { 
        context: 'useLinkPreview - getDomainFallback',
        url,
        urlType: typeof url
      }
    });
    return url;
  }
}

/**
 * Fallback function to get favicon URL (used when API fails)
 * @param {string} url 
 * @returns {string|null}
 */
export function getFaviconFallback(url) {
  try {
    const domain = new URL(url).origin;
    return `${domain}/favicon.ico`;
  } catch (error) {
    Sentry.captureException(error, {
      extra: { 
        context: 'useLinkPreview - getFaviconFallback',
        url,
        urlType: typeof url
      }
    });
    return null;
  }
}